<template>
    <CategoryTop :title="$t('global.nav.favoriteProducts')" :description="$t('shop.favoriteProducts.description')"
        :breadcrumbs-parent-routes="['account']">
    </CategoryTop>
    <Loader :show="loading" v-if="loading"></Loader>
    <div v-else>
        <MDBRow>
            <MDBCol col="12" lg="8">
                <div class="pt-2">
                    <MDBTable borderless class="points-overview">
                        <thead>
                            <tr>
                                <th>Status</th>
                                <th>{{ $t("shop.order.products") }}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody v-if="productList.length > 0">
                            <tr v-for="(data, index) in productList" :key="index">
                                <td>
                                    <template v-if="data.product.status === PRODUCT_STATUS.VISIBLE_NOT_ALLOWED_TO_BUY">
                                        {{ getNotAvailabilityMessage(data.product) }}
                                    </template>
                                    <template v-else-if="data.product.status !== PRODUCT_STATUS.ACTIVE">
                                        {{ $t("shop.timeUnavailable") }}
                                    </template>
                                    <template v-else>
                                        <router-link :to="{
                                            name: 'productDetails',
                                            params: {
                                                id: data.product.id,
                                            },
                                        }">
                                            {{ $t("global.buttons.clickHere") }}
                                        </router-link>

                                    </template>
                                </td>
                                <td>
                                    <router-link
                                        :to="{
                                            name: 'productDetails',
                                            params: {
                                                id: data.product.id,
                                            },
                                        }"
                                        class="favorite-product-link"
                                    >
                                        {{ data.product.name }}
                                        <b v-if="data.productVariant">
                                            {{ showAttributes(data.productVariant.attribute_option) }}
                                        </b>
                                    </router-link>
                                </td>
                                <td class="text-end">
                                    <MDBBtn
                                        color="link"
                                        size="sm"
                                        class="favorite-product-remove text-danger p-0"
                                        :disabled="favoriteRemoveBusy[itemKey(data)]"
                                        @click="removeFavorite(data)"
                                    >
                                        {{ $t("global.buttons.delete") }}
                                    </MDBBtn>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <div>{{ $t("shop.order.anyOrder") }}</div>
                        </tbody>
                    </MDBTable>
                </div>
            </MDBCol>
        </MDBRow>
    </div>
</template>

<script setup>
import CategoryTop from "@/components/General/CategoryTop/index.vue";
import { MDBRow, MDBCol, MDBTable, MDBBtn } from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { onMounted, computed, ref, watch } from "vue";
import Loader from "@/components/General/Loader/index.vue";
import dates from "@/plugins/dates";
import { ProductService } from "@/services/product.service";
import { PRODUCT_STATUS } from "@/configs/constants";
import { useI18n } from "vue-i18n";
import { toast } from "vue3-toastify";

const store = useStore();
const { t } = useI18n();

const user = computed(() => store.getters.getMe);
const loading = computed(() => store.getters.getPending);
const currentLocale = computed(() => store.getters.getCurrentLocale);
const selectedProduct = ref(null);
const query = ref("");
const date = ref(null);
const favoriteRemoveBusy = ref({});

const productList = ref([]);

const itemKey = (item) =>
    `${item?.product_id ?? item?.product?.plainId ?? item?.product?.id}_${
        item?.product_variant_id ?? item?.productVariant?.id ?? "none"
    }`;

onMounted(async () => {
    const response = await ProductService.userFavoriteProducts(user.value.plainId)
    productList.value = response.data.data
});

const getNotAvailabilityMessage = (product) => {
    const translations = product?.not_availability_information;
    const localized =
        translations && typeof translations === "object"
            ? translations[currentLocale.value]
            : typeof translations === "string"
                ? translations
                : "";

    if (typeof localized === "string" && localized.trim()) {
        return localized.trim();
    }

    return t("shop.outOfStock");
};

const showAttributes = (attribute_option) => {
    if (!attribute_option) {
        return "";
    }

    let parsedAttributes = [];
    try {
        parsedAttributes =
            typeof attribute_option === "string"
                ? JSON.parse(attribute_option)
                : Array.isArray(attribute_option)
                    ? attribute_option
                    : [];
    } catch (error) {
        return "";
    }

    if (!Array.isArray(parsedAttributes)) {
        return "";
    }

    return parsedAttributes
        .map((item) => `${item?.label ?? ""}: ${item?.option ?? ""}`.trim())
        .filter((item) => item !== ":" && item.length > 0)
        .join(", ");
};

const removeFavorite = async (item) => {
    const key = itemKey(item);
    if (favoriteRemoveBusy.value[key]) {
        return;
    }

    favoriteRemoveBusy.value = {
        ...favoriteRemoveBusy.value,
        [key]: true,
    };

    try {
        const data = {
            user_id: user.value?.plainId,
            product_id: item?.product_id ?? item?.product?.plainId ?? item?.product?.id,
            product_variant_id:
                item?.product_variant_id ?? item?.productVariant?.id ?? null,
        };

        const response = await ProductService.saveDeleteFavorite(data);
        if (!response || response.status >= 400) {
            const message =
                response?.data?.message || response?.data?.error || t("toast.waitMoment");
            toast(message, {
                autoClose: 8000,
                type: "error",
                theme: "colored",
                transition: toast.TRANSITIONS.SLIDE,
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            return;
        }

        productList.value = productList.value.filter(
            (favoriteItem) => itemKey(favoriteItem) !== key,
        );
        await store.dispatch("me");
        toast(t("toast.favoriteRemoved"), {
            autoClose: 5000,
            type: "success",
            theme: "colored",
            transition: toast.TRANSITIONS.SLIDE,
            position: toast.POSITION.BOTTOM_RIGHT,
        });
    } finally {
        favoriteRemoveBusy.value = {
            ...favoriteRemoveBusy.value,
            [key]: false,
        };
    }
};

const search = async () => {
    const params = [];
    selectedProduct.value
        ? params.push(`productId=${selectedProduct.value}`)
        : "";
    query.value ? params.push(`search=${query.value}`) : "";
    date.value ? params.push(`date=${dates.parseDate(date.value)}`) : "";

    let data = "?";
    for (const element of params) {
        data += `${element}&`;
    }

    await store.dispatch("loadUserOrders", data.replace(/.$/, ""));
};

watch(query, async (newVal) => {
    if (newVal.length == 0) {
        await search();
    }
});
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";

.points-overview {
    width: 100%;
    border-spacing: 3px;
    border-collapse: separate;

    td,
    th {
        padding: 5px;
        text-align: left;
    }

    th {
        background: $gray;
        color: $backgroundGray;
    }
}

.points-overview>tbody>tr:nth-of-type(2n + 1)>* {
    background-color: $secondaryGray;
}

.favorite-product-link {
    color: inherit;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.favorite-product-remove {
    font-size: 12px;
}
</style>
